export default class BackofficeStudyRequestsEdit {
  constructor() {
    this.cacheElements();
    this.initializeSelect2();
    this.bindEvents();
    this.applyEntityDataset(this.currentEntityId, true);
    this.updateProductPricing();
  }

  cacheElements() {
    this.$entitySelect = $("#study_request_entity_select");
    this.$productSelect = $("#study_request_product_select");
    this.$authorSelect = $("#study_request_author_select");
    this.$engagementCell = $("#engagement_product");
    this.$fasProductCell = $("#fas_product");
    this.$subscriptionProductCell = $("#subscription_product");
    this.$fasTotalCell = $("#fas_total");
    this.$subscriptionTotalCell = $("#subscription_total");
    this.$fonContainer = $("#fon-fields-container");

    const $dataset = $("#study-request-edit-datasets");
    this.entityData = this.safeParseJSON($dataset.attr("data-entities"));
    this.productPricing = this.safeParseJSON($dataset.attr("data-product-pricing"));
    this.currentEntityId = $dataset.attr("data-current-entity") || this.$entitySelect.val();
    this.currentAuthorId = $dataset.attr("data-current-author") || this.$authorSelect.val();
    this.hasCustomPricing = $dataset.attr("data-has-custom-pricing") === "true";
    this.hasFar = $dataset.attr("data-has-far") === "true";
    this.hasIru = $dataset.attr("data-has-iru") === "true";
    this.customPricing = this.safeParseJSON($dataset.attr("data-custom-pricing"));
    this.productMetadata = {};
  }

  initializeSelect2() {
    if (this.$entitySelect.length) {
      this.$entitySelect.select2({
        width: "100%",
        placeholder: this.$entitySelect.data("placeholder") || "Sélectionner une entité"
      });
    }

    if (this.$productSelect.length) {
      this.$productSelect.select2({
        width: "100%",
        placeholder: this.$productSelect.data("placeholder") || "Sélectionner un produit"
      });
    }

    if (this.$authorSelect.length) {
      this.$authorSelect.select2({
        width: "100%",
        placeholder: this.$authorSelect.data("placeholder") || "Sélectionner un auteur",
        allowClear: true
      });
    }
  }

  bindEvents() {
    if (this.$entitySelect.length) {
      this.$entitySelect.on("change", () => {
        const entityId = this.$entitySelect.val();
        this.applyEntityDataset(entityId, true);
      });
    }

    if (this.$productSelect.length) {
      this.$productSelect.on("change", () => {
        this.updateProductPricing();
      });
    }
  }

  applyEntityDataset(entityId, preserveSelections = true) {
    const entityKey = entityId ? String(entityId) : "";
    this.currentEntityId = entityKey;

    if (!entityKey || entityKey === "null" || entityKey === "undefined") {
      this.productPricing = {};
      this.refreshProductList([], false);
      this.refreshAuthorList([], false);
      this.toggleFonFields(false);
      return;
    }

    const payload = this.entityData[entityKey] || {};
    const products = payload.products || [];
    const customers = payload.customers || [];

    this.productPricing = payload.product_pricing || {};
    this.productMetadata = {};
    products.forEach(product => {
      this.productMetadata[String(product.id)] = product;
    });

    this.refreshProductList(products, preserveSelections);
    this.refreshAuthorList(customers, preserveSelections);
    this.updateProductPricing();
  }

  refreshProductList(products, preserveSelections) {
    const previousProduct = this.$productSelect.val();
    let existingSelection = null;
    
    if (preserveSelections && previousProduct) {
      const productExists = products.some(product => String(product.id) === String(previousProduct));
      if (productExists) {
        existingSelection = previousProduct;
      }
    }

    if (this.$productSelect.hasClass("select2-hidden-accessible")) {
      this.$productSelect.off("change.select2");
      this.$productSelect.select2("destroy");
    }

    this.$productSelect.empty();

    products.forEach(product => {
      const option = new Option(product.name, product.id, false, false);
      option.dataset.fas = product.fas;
      option.dataset.subscription = product.subscription;
      option.dataset.engagement = product.engagement;
      option.dataset.isFon = product.is_fon ? "1" : "0";
      this.$productSelect.append(option);
    });

    if (existingSelection) {
      this.$productSelect.val(existingSelection);
    }

    this.$productSelect.select2({
      width: "100%",
      placeholder: this.$productSelect.data("placeholder") || "Sélectionner un produit"
    });

    this.$productSelect.trigger("change.select2");
    this.$productSelect.trigger("change");
  }

  refreshAuthorList(customers, preserveSelections) {
    const previousAuthor = this.$authorSelect.val();
    const existingSelection = preserveSelections && customers.some(customer => String(customer.id) === String(previousAuthor))
      ? previousAuthor
      : null;

    if (this.$authorSelect.hasClass("select2-hidden-accessible")) {
      this.$authorSelect.off("change.select2");
      this.$authorSelect.select2("destroy");
    }

    this.$authorSelect.empty();
    this.$authorSelect.append(new Option("", "", false, false));

    customers.forEach(customer => {
      this.$authorSelect.append(new Option(customer.name, customer.id, false, false));
    });

    if (existingSelection) {
      this.$authorSelect.val(existingSelection);
    }

    this.$authorSelect.select2({
      width: "100%",
      placeholder: this.$authorSelect.data("placeholder") || "Sélectionner un auteur",
      allowClear: true
    });

    this.$authorSelect.trigger("change.select2");
  }

  updateProductPricing() {
    const productId = this.$productSelect.val();
    const pricing = this.productPricing[productId] || { fas: 0, subscription: 0, engagement: "--" };
    const metadata = this.productMetadata[String(productId)] || {};
    let isFon = metadata.is_fon;

    if (isFon === undefined) {
      const optionEl = this.$productSelect.find("option:selected").get(0);
      if (optionEl) {
        isFon = optionEl.dataset.isFon === "1";
      }
    }

    // If custom pricing exists (with FAR/IRU), do not update the pricing cells
    // The custom pricing values should remain as rendered by the server
    if (this.hasCustomPricing && (this.hasFar || this.hasIru)) {
      this.toggleFonFields(Boolean(isFon));
      return;
    }

    this.$engagementCell.text(pricing.engagement || "--");
    this.$fasProductCell.attr("data-price", pricing.fas || 0);
    this.$fasProductCell.text(I18n.toCurrency(pricing.fas || 0));
    this.$subscriptionProductCell.attr("data-price", pricing.subscription || 0);
    this.$subscriptionProductCell.text(I18n.toCurrency(pricing.subscription || 0));

    this.updateTotals();
    this.toggleFonFields(Boolean(isFon));
  }

  updateTotals() {
    // If custom pricing exists (with FAR/IRU), use the custom pricing totals
    if (this.hasCustomPricing && (this.hasFar || this.hasIru)) {
      const fasTotal = this.customPricing.total_one_time || 0;
      const subscriptionTotal = this.customPricing.recurrent_price || 0;

      this.$fasTotalCell.attr("data-price", fasTotal);
      this.$fasTotalCell.text(I18n.toCurrency(fasTotal));

      this.$subscriptionTotalCell.attr("data-price", subscriptionTotal);
      this.$subscriptionTotalCell.text(I18n.toCurrency(subscriptionTotal));
      return;
    }

    const fasTotal = Number.parseFloat(this.$fasProductCell.attr("data-price")) || 0;
    const subscriptionTotal = Number.parseFloat(this.$subscriptionProductCell.attr("data-price")) || 0;

    this.$fasTotalCell.attr("data-price", fasTotal);
    this.$fasTotalCell.text(I18n.toCurrency(fasTotal));

    this.$subscriptionTotalCell.attr("data-price", subscriptionTotal);
    this.$subscriptionTotalCell.text(I18n.toCurrency(subscriptionTotal));
  }

  safeParseJSON(payload) {
    if (!payload) {
      return {};
    }

    try {
      return JSON.parse(payload);
    } catch (error) {
      console.error("Failed to parse JSON payload", error);
      return {};
    }
  }

  toggleFonFields(isFon) {
    if (!this.$fonContainer || !this.$fonContainer.length) {
      return;
    }

    if (isFon) {
      this.$fonContainer.removeClass("d-none");
      this.$fonContainer.find("[data-fon-required]").each((_, element) => {
        element.removeAttribute("disabled");
        element.disabled = false;
        element.setAttribute("required", "required");
        element.required = true;
      });
    } else {
      this.$fonContainer.addClass("d-none");
      this.$fonContainer.find("[data-fon-required]").each((_, element) => {
        element.removeAttribute("required");
        element.required = false;
        element.setAttribute("disabled", "disabled");
        element.disabled = true;
      });
    }
  }

  onDataChanged() {}

  onDestroy() {
    if (this.$entitySelect && this.$entitySelect.hasClass("select2-hidden-accessible")) {
      this.$entitySelect.select2("destroy");
    }

    if (this.$productSelect && this.$productSelect.hasClass("select2-hidden-accessible")) {
      this.$productSelect.select2("destroy");
    }

    if (this.$authorSelect && this.$authorSelect.hasClass("select2-hidden-accessible")) {
      this.$authorSelect.select2("destroy");
    }
  }
}
